import type { ReactNode } from "react";
import { PhoneIllustration } from "./PhoneIllustration";
import { Wordmark, buttonClass } from "../components/ui";
import { MONTHLY_PRICE } from "../features/billing/pricing";
import { SUPPORT_EMAIL } from "../lib/contact";
import { FREE_BETA } from "../lib/featureFlags";
import imgCaseStudy from "../assets/landing/787a1.png";
import imgWorkspace from "../assets/landing/f3c1e.png";
import iconImage from "../assets/landing/b6a33.svg";
import iconText from "../assets/landing/0f58f.svg";

type LandingPageProps = {
  onLogin: () => void;
  onStart: () => void;
};

/*
 * Phones get one narrow column. From tablet width the column opens up to
 * 1200px; portrait tablets (sm to lg) centre the hero, and desktops (lg)
 * split the hero and the feature sections into two columns.
 */
const ContentColumn = ({ children }: { children: ReactNode }) => (
  <div className="mx-auto w-full max-w-[420px] sm:max-w-[1200px] sm:px-6">{children}</div>
);

const sectionClass =
  "flex flex-col gap-6 px-6 py-14 sm:gap-8 sm:py-[clamp(64px,7vw,104px)]";

const SectionHeader = ({
  index,
  label,
  inverted = false,
  className = "",
}: {
  index: string;
  label: string;
  inverted?: boolean;
  className?: string;
}) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span
      className={`font-lora text-[28px] leading-none opacity-30 ${
        inverted ? "text-hazumu-white" : "text-hazumu-ink"
      }`}
    >
      {index}
    </span>
    <div
      className={`h-px flex-1 ${
        inverted ? "bg-hazumu-white/15" : "bg-hazumu-ink/10"
      }`}
    />
    <span className="font-sans text-[10.5px] font-semibold uppercase tracking-[0.84px] text-hazumu-terracotta">
      {label}
    </span>
  </div>
);

const SectionTitle = ({
  children,
  inverted = false,
  className = "",
}: {
  children: ReactNode;
  inverted?: boolean;
  className?: string;
}) => (
  <h2
    className={`font-lora text-[28px] font-semibold leading-[1.22] sm:max-w-[20ch] sm:text-[clamp(32px,3.5vw,44px)] sm:tracking-[-0.025em] ${
      inverted ? "text-hazumu-canvas" : "text-hazumu-ink"
    } ${className}`}
  >
    {children}
  </h2>
);

const SectionText = ({
  children,
  inverted = false,
  className = "",
}: {
  children: ReactNode;
  inverted?: boolean;
  className?: string;
}) => (
  <p
    className={`font-sans text-[13px] leading-[1.6] sm:max-w-[52ch] sm:text-[16px] sm:leading-[1.7] ${
      inverted ? "text-hazumu-canvas opacity-80" : "text-hazumu-muted"
    } ${className}`}
  >
    {children}
  </p>
);

const footerLinkClass =
  "inline-flex min-h-11 items-center rounded-sm font-sans text-[11.5px] font-medium text-hazumu-muted underline decoration-hazumu-clay underline-offset-2 transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink";

const Accent = ({ children }: { children: ReactNode }) => (
  <em className="font-normal italic text-hazumu-terracotta">{children}</em>
);

/*
 * The dark pill is the kit's primary button. The light pill sits on the ink
 * footer band, so it keeps a white fill and a white focus outline, in the
 * kit's large pill shape.
 */
const pillButtonClasses = {
  dark: buttonClass({ variant: "primary", size: "lg", pill: true }),
  light:
    "inline-flex min-h-12 items-center justify-center whitespace-nowrap rounded-full bg-hazumu-white px-5 py-3 text-sm font-semibold text-hazumu-ink transition-colors hover:bg-hazumu-blush focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-white",
} as const;

const PillButton = ({
  children,
  onClick,
  variant = "dark",
}: {
  children: ReactNode;
  onClick: () => void;
  variant?: "dark" | "light";
}) => (
  <button
    className={`shrink-0 font-sans ${pillButtonClasses[variant]}`}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const principles = [
  {
    title: "In your pocket",
    body: "An editor designed purely for mobile. Add blocks, write thoughts, upload photos directly from your camera roll.",
  },
  {
    title: "Feels like you",
    body: "Hand-tuned themes and considered block styles. Pick a look, then let your words and pictures breathe like a book.",
  },
  {
    title: "Built to last",
    body: "Fast, light, and optimised to load instantly. Your site is hosted on global edge servers automatically.",
  },
];

const audiences = [
  {
    title: "Freelancers & consultants",
    body: "Show the work, and make getting in touch effortless.",
  },
  {
    title: "Creators & artists",
    body: "A clean canvas for your projects, essays, and ideas.",
  },
  {
    title: "Side projects",
    body: "Validate ideas quickly without fighting complex layout tools.",
  },
];

export const LandingPage = ({ onLogin, onStart }: LandingPageProps) => (
  <main className="min-h-screen bg-hazumu-canvas font-sans text-hazumu-ink">
    <header className="border-b border-hazumu-border bg-hazumu-canvas">
      <ContentColumn>
        <div className="flex items-center justify-between px-6 py-5">
          <Wordmark size="sm" />
          <button
            className="-my-3 inline-flex min-h-11 items-center rounded-sm px-2 -mr-2 font-sans text-[13px] font-medium text-hazumu-muted transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink"
            onClick={onLogin}
            type="button"
          >
            Log in
          </button>
        </div>
      </ContentColumn>
    </header>

    <section className="bg-hazumu-canvas">
      <ContentColumn>
        <div className="flex flex-col gap-6 px-6 pt-12 sm:max-lg:items-center sm:max-lg:gap-10 lg:grid lg:grid-cols-2 lg:items-center lg:gap-[clamp(32px,6vw,88px)] lg:pt-16">
          <div className="flex flex-col gap-6 sm:max-lg:max-w-[520px] sm:max-lg:items-center sm:max-lg:text-center lg:max-w-[560px] lg:pb-16">
            <div className="flex flex-col gap-3">
              <h1 className="font-lora text-[38px] font-semibold leading-[1.15] text-hazumu-ink sm:tracking-[-0.035em] sm:max-lg:mx-auto sm:max-lg:max-w-[18ch] sm:max-lg:text-balance sm:max-lg:text-[clamp(44px,5.6vw,52px)] lg:text-[clamp(44px,5.2vw,68px)]">
                Your personal site, built in <Accent>minutes.</Accent>
              </h1>
              <p className="font-sans text-[14px] leading-[1.55] text-hazumu-muted sm:text-[16px] sm:leading-[1.7] sm:max-lg:mx-auto sm:max-lg:max-w-[46ch] lg:max-w-[52ch]">
                Not a store. Not a template. A website that reads like you wrote
                it — because you did, from your phone, in the time it takes to
                make a coffee.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:max-lg:justify-center sm:max-lg:text-left">
              <PillButton onClick={onStart}>Start building</PillButton>
              <div className="flex flex-col gap-0.5 font-sans text-[11.5px] leading-normal text-hazumu-muted">
                <p className="font-medium">{FREE_BETA ? "Free during beta." : "Free to design."}</p>
                <p className="opacity-70">No code, no laptop.</p>
              </div>
            </div>
          </div>
          {/* Scales the exported artwork as a unit, keeping its masks and positions. */}
          <div className="@container aspect-[358/540] w-full max-w-[358px] self-center sm:max-lg:max-w-[290px] lg:self-end lg:justify-self-center [&>div]:origin-top-left [&>div]:scale-[calc(100cqw/358px)]">
            <PhoneIllustration />
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-border/30">
      <ContentColumn>
        <div className={sectionClass}>
          <SectionHeader index="01" label="Three principles" />
          <SectionTitle>
            What makes it <Accent>different</Accent>
          </SectionTitle>
          <div className="flex flex-col gap-4 md:grid md:grid-cols-3 md:gap-6">
            {principles.map((principle) => (
              <article
                className="flex flex-col gap-2 rounded-2xl border border-hazumu-border bg-hazumu-white p-5 md:p-[clamp(20px,2.5vw,32px)]"
                key={principle.title}
              >
                <h3 className="font-lora text-[18px] font-semibold text-hazumu-ink sm:text-[21px]">
                  {principle.title}
                </h3>
                <p className="font-sans text-[12px] leading-[1.5] text-hazumu-muted sm:text-[14px] sm:leading-[1.7]">
                  {principle.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-ink">
      <ContentColumn>
        <div className={`${sectionClass} lg:grid lg:grid-cols-2 lg:content-center lg:gap-x-20`}>
          <SectionHeader className="lg:col-span-full lg:mb-4" index="02" inverted label="Why it matters" />
          <SectionTitle className="lg:col-start-1" inverted>
            Give your best work <Accent>room</Accent> to breathe
          </SectionTitle>
          <SectionText className="lg:col-start-1" inverted>
            A thumbnail and a caption isn&rsquo;t a portfolio. Each project,
            essay or case study gets a full page of its own — its own words, its
            own images, its own address to send someone. Gather them into a
            library and they arrange themselves.
          </SectionText>
          <div className="-mx-6 overflow-x-auto px-6 [scrollbar-width:none] lg:col-start-1 [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max items-start gap-2 sm:w-auto sm:flex-wrap">
              {["Case studies", "Photography", "Writing", "Services"].map(
                (category) => (
                  <span
                    className="rounded-full border border-hazumu-white/20 px-4 py-2 font-sans text-[12px] text-hazumu-canvas"
                    key={category}
                  >
                    {category}
                  </span>
                ),
              )}
            </div>
          </div>
          <div className="-mx-6 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:px-0 lg:col-start-2 lg:row-span-3 lg:row-start-2 lg:self-center [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max items-start gap-3 sm:grid sm:w-full sm:grid-cols-2 sm:gap-5">
              <img
                alt="Project page layout on a personal site"
                className="h-[240px] w-[180px] rounded-xl object-cover sm:aspect-[3/4] sm:h-auto sm:w-full"
                src={imgCaseStudy}
              />
              <img
                alt="Photography page layout on a personal site"
                className="h-[240px] w-[180px] rounded-xl object-cover sm:aspect-[3/4] sm:h-auto sm:w-full"
                src={imgWorkspace}
              />
            </div>
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-canvas">
      <ContentColumn>
        <div className={`${sectionClass} lg:grid lg:grid-cols-2 lg:content-center lg:gap-x-20`}>
          <SectionHeader className="lg:col-span-full lg:mb-4" index="03" label="Mobile first" />
          <SectionTitle className="lg:col-start-1">
            Designed for the <Accent>phone</Accent>
          </SectionTitle>
          <SectionText className="lg:col-start-1">
            You do your best work on the move. We built Hazumu so you can
            capture details, draft notes, and publish live pages without ever
            needing to open a laptop.
          </SectionText>
          <div className="flex flex-col gap-4 rounded-2xl border border-hazumu-border bg-hazumu-border/30 p-5 lg:col-start-2 lg:row-span-2 lg:row-start-2 lg:gap-6 lg:self-center lg:p-8">
            <div className="flex items-center justify-between">
              <p className="font-sans text-[12px] font-semibold text-hazumu-ink lg:text-[14px]">
                Site editor
              </p>
              <span className="rounded bg-hazumu-white px-2 py-1 font-sans text-[10px] font-semibold text-hazumu-terracotta">
                Saved
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between rounded-lg bg-hazumu-white p-3">
                <p className="font-sans text-[12px] text-hazumu-ink lg:text-[14px]">
                  + Add image
                </p>
                <img alt="" className="size-[14px]" src={iconImage} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-hazumu-white p-3">
                <p className="font-sans text-[12px] text-hazumu-ink lg:text-[14px]">
                  + Add text
                </p>
                <img alt="" className="size-[14px]" src={iconText} />
              </div>
            </div>
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-border/30">
      <ContentColumn>
        <div className={sectionClass}>
          <SectionHeader index="04" label="Who it's for" />
          <SectionTitle>
            However you <Accent>work</Accent>
          </SectionTitle>
          <div className="flex flex-col gap-4 md:grid md:grid-cols-3 md:gap-6">
            {audiences.map((audience, index) => (
              <div
                className="flex flex-col gap-1.5 border-b border-hazumu-ink/8 pb-4 md:gap-4 md:pb-6"
                key={audience.title}
              >
                <div className="flex items-center gap-2">
                  <span className="font-sans text-[11px] font-semibold text-hazumu-terracotta">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-lora text-[16px] font-semibold text-hazumu-ink sm:text-[21px]">
                    {audience.title}
                  </h3>
                </div>
                <p className="font-sans text-[12.5px] leading-[1.5] text-hazumu-muted sm:text-[14px] sm:leading-[1.7]">
                  {audience.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-canvas">
      <ContentColumn>
        <div className={`${sectionClass} md:grid md:grid-cols-2 md:items-start md:gap-x-16`}>
          <SectionHeader className="md:col-span-full" index="05" label="Pricing" />
          <SectionTitle>
            {FREE_BETA ? (
              <>Free while we&rsquo;re in <Accent>beta</Accent></>
            ) : (
              <>Free to design, <Accent>simple</Accent> to launch</>
            )}
          </SectionTitle>
          <div className="flex flex-col items-center gap-5 rounded-[20px] border border-hazumu-border bg-hazumu-white p-7 sm:p-10 md:col-start-2">
            <div className="flex flex-col items-center gap-1">
              <p className="font-lora text-[44px] font-semibold leading-none text-hazumu-ink">
                {FREE_BETA ? "Free" : MONTHLY_PRICE}
              </p>
              <p className="font-sans text-[12px] text-hazumu-muted">
                {FREE_BETA ? "every feature, during beta" : "a month, once your site is live"}
              </p>
            </div>
            <p className="text-center font-sans text-[11.5px] leading-[1.5] text-hazumu-muted sm:max-w-[38ch] sm:text-[14px]">
              {FREE_BETA
                ? `After launch, designing your site stays free. When you’re ready to go live, it’s ${MONTHLY_PRICE} a month. Thirty days’ notice before anything changes.`
                : "Design your site for free, for as long as you like. Subscribe when you’re ready to go live. Cancel whenever."}
            </p>
            <PillButton onClick={onStart}>Start free</PillButton>
          </div>
        </div>
      </ContentColumn>
    </section>

    <section className="bg-hazumu-ink">
      <ContentColumn>
        <div className="flex flex-col items-center gap-6 px-6 pb-20 pt-16 sm:py-24">
          <h2 className="text-center font-lora text-[32px] font-semibold leading-[1.15] text-hazumu-canvas sm:text-[clamp(40px,4.5vw,56px)] sm:tracking-[-0.025em]">
            Start your space <Accent>today.</Accent>
          </h2>
          <SectionText className="text-center" inverted>
            Create a space that feels like yours. Built cleanly, optimised for
            speed, updated wherever you are.
          </SectionText>
          <PillButton onClick={onStart} variant="light">
            Start building
          </PillButton>
        </div>
      </ContentColumn>
    </section>

    <footer className="border-t border-hazumu-border bg-hazumu-canvas">
      <ContentColumn>
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-10">
          <Wordmark size="sm" />
          <div className="flex flex-col items-end gap-1.5">
            <p className="font-sans text-[11.5px] text-hazumu-muted">
              © 2026 Mythical Box Ltd. All rights reserved.
            </p>
            <div className="-my-2 flex gap-3">
              <a className={footerLinkClass} href="/privacy">
                Privacy policy
              </a>
              <a className={footerLinkClass} href={`mailto:${SUPPORT_EMAIL}`}>
                Contact
              </a>
            </div>
          </div>
        </div>
      </ContentColumn>
    </footer>
  </main>
);
